//
import type { AccountStoreState, AuthStoreState } from '@/types/Store'
import type { App } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import { markRaw } from 'vue'
import { useAccountStore } from '@/store/account'
import { useAuthStore } from '@/store/auth'

const debug = import.meta.env.VITE_ENV !== 'production'
const persistedKey = 'pinia'

const pinia = createPinia()
setActivePinia(pinia)

let hydrationPerformed = false
let persistenceInitialized = false

const hydrateState = () => {
  if (hydrationPerformed) {
    return
  }

  try {
    const rawState = localStorage.getItem(persistedKey)
    if (!rawState) {
      hydrationPerformed = true
      return
    }

    const data = JSON.parse(rawState) as {
      account?: { account: AccountStoreState['account'] }
      auth?: AuthStoreState
    }

    const accountStore = useAccountStore(pinia)
    const authStore = useAuthStore(pinia)

    if (data.account?.account) {
      accountStore.$patch({ account: data.account.account })
    }

    if (data.auth) {
      authStore.$patch(data.auth)
    }
  } catch (error) {
    if (debug) {
      console.warn('Hydration error', error)
    }
  } finally {
    hydrationPerformed = true
  }
}

const initializePersistence = () => {
  if (persistenceInitialized) {
    return
  }

  const accountStore = useAccountStore(pinia)
  const authStore = useAuthStore(pinia)

  const persistState = () => {
    try {
      const payload = {
        account: { account: accountStore.account },
        auth: authStore.$state,
      }

      localStorage.setItem(persistedKey, JSON.stringify(payload))
    } catch (error) {
      if (debug) {
        console.warn('Persist state error', error)
      }
    }
  }

  accountStore.$subscribe(persistState, { detached: true })
  authStore.$subscribe(persistState, { detached: true })
  persistState()

  persistenceInitialized = true
}

const store = {
  install(app: App) {
    app.use(pinia)
    //todo: remove
    hydrateState()
    initializePersistence()
  },
  pinia,
}

export type Store = typeof store

export default markRaw(store)
